import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest, userRequest } from "../utils/utils";
import { ICountryList, ILoginResponse, IRegisterParams } from "../types";
import { RootState } from "./store";

interface IAuthState {
  message: string;
  email: string | null;
  employeeEmail: string | null;
  access: string | null;
  refresh: string | null;
  role: string;
  fullname: string;
  countries: ICountryList[];
}

const initialState: IAuthState = {
  message: "",
  email: null,
  employeeEmail: null,
  access: null,
  refresh: null,
  role: "",
  fullname: "",
  countries: [],
};

export const register = createAsyncThunk<
  { message: string; email: string },
  IRegisterParams,
  { rejectValue: { error: string } }
>("auth/register", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `/users/api/auth/register/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const registerEmployee = createAsyncThunk<
  { message: string; email: string },
  IRegisterParams,
  { rejectValue: { error: string } }
>("auth/registerEmployee", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `/users/api/auth/register_user_to_manager/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const verifyEmail = createAsyncThunk<
  { message: string },
  { email: string; otp: string },
  { rejectValue: { error: string } }
>("auth/verifyEmail", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `users/api/auth/verify_email/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const resendOtp = createAsyncThunk<
  { message: string },
  { email: string },
  { rejectValue: { error: string } }
>("auth/resendOtp", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `/users/api/auth/resend_email_verification/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const login = createAsyncThunk<
  ILoginResponse,
  { email: string; password: string },
  { rejectValue: { error: string } }
>("auth/login", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(`users/api/auth/login/`, params);
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const resetPassword = createAsyncThunk<
  { message: string },
  { email: string },
  { rejectValue: { error: string } }
>("auth/resetPassword", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `users/api/auth/reset_password/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const confirmPassword = createAsyncThunk<
  { message: string },
  { token: string; new_password: string; confirm_new_password: string },
  { rejectValue: { error: string } }
>("auth/confirmNewPassword", async (params, { rejectWithValue }) => {
  try {
    const { data } = await publicRequest.post(
      `users/api/auth/set_new_password/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const changeEmail = createAsyncThunk<
  { message: string },
  { new_email: string; password: string },
  { rejectValue: { error: string } }
>("auth/changeEmail", async (params, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.post(
      `users/api/auth/change_email/`,
      params
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const verifyNewEmail = createAsyncThunk<
  { message: string },
  { otp: string },
  { rejectValue: { error: string } }
>("auth/verifyNewEmail", async (params, { rejectWithValue, getState }) => {
  try {
    const access = (getState() as RootState).auth.access;
    const { data } = await publicRequest.post(
      `users/api/auth/confirm_email_change/`,
      params,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access}`,
        },
      }
    );
    return data;
  } catch (err) {
    const error = err as {
      response?: {
        data?: { error?: string; non_field_errors?: string[] };
      };
    };

    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.non_field_errors?.join(", ") ||
      "An unknown error occurred";
    return rejectWithValue({ error: errorMessage });
  }
});

export const refreshToken = createAsyncThunk<
  { access: string; refresh: string },
  void,
  { rejectValue: { error: string } }
>(
  "auth/refreshToken",

  async (_, { getState, rejectWithValue }) => {
    try {
      const refresh = (getState() as RootState).auth.refresh;
      const { data } = await publicRequest.post(
        `/users/api/auth/refresh_token/`,
        { refresh }
      );
      return data;
    } catch (err) {
      const error = err as {
        response?: {
          data?: { error?: string; non_field_errors?: string[] };
        };
      };

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.non_field_errors?.join(", ") ||
        "An unknown error occurred";
      return rejectWithValue({ error: errorMessage });
    }
  }
);

export const getCountriesList = createAsyncThunk<
  ICountryList[],
  void,
  { rejectValue: { error: string } }
>("auth/getCountries", async (page, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(`/dashboard/api/countries`);
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.access = null;
      state.refresh = null;
      state.email = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(register.fulfilled, (state, action) => {
        state.message = action.payload?.message;
        state.email = action.payload?.email;
      })
      .addCase(registerEmployee.fulfilled, (state, action) => {
        state.message = action.payload?.message;
        state.employeeEmail = action.payload?.email;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.access = action.payload.access;
        state.refresh = action.payload.refresh;
        state.email = action.payload.email;
        state.role = action.payload.role;
        state.fullname = action.payload.full_name;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.access = action.payload.access;
        state.refresh = action.payload.refresh;
      })

      .addCase(resetPassword.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(confirmPassword.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(changeEmail.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(verifyNewEmail.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.message = action.payload?.message;
      })
      .addCase(getCountriesList.fulfilled, (state, action) => {
        state.countries = action.payload;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
